/**
 * eventNormalizer — single source of truth for turning canonical
 * AgentEvent rows (from cloud SSE) and vendor share-session frames
 * (from browser-direct remote-attach) into UI-ready cards.
 *
 * The canonical AgentEvent is defined by the agent's bot.AgentEvent
 * Go type and persisted in cloud's `agent_events` table. The vendor
 * share-session shape is whatever Anthropic / OpenAI emits today; we
 * try to fold it into the same UICard set so <StructuredChat> stays
 * mode-agnostic.
 */

export type UICard =
  | { kind: 'system'; text: string; seq: number }
  | { kind: 'turn_start'; seq: number }
  | { kind: 'message'; role: 'assistant' | 'user'; text: string; seq: number }
  | { kind: 'tool_use'; name: string; input: unknown; id?: string; seq: number }
  | { kind: 'tool_result'; toolUseId?: string; content: unknown; seq: number }
  | { kind: 'permission_request'; toolUseId?: string; name?: string; input?: unknown; seq: number }
  | { kind: 'usage'; usage: unknown; seq: number }
  | { kind: 'turn_end'; isError?: boolean; cost?: number; seq: number }
  | { kind: 'error'; message: string; details?: unknown; seq: number };

export interface CanonicalAgentEvent {
  event_id?: string;
  instance_id?: string;
  session_id?: string;
  seq: number;
  kind: string;
  payload: unknown;
  created_at?: number;
}

/**
 * Convert a canonical AgentEvent (cloud SSE) into a UICard.
 * Unknown kinds return null (caller can fall back to a debug renderer).
 */
export function normalizeCanonical(ev: CanonicalAgentEvent): UICard | null {
  const payload = parseIfString(ev.payload);
  switch (ev.kind) {
    case 'turn_start':
      return { kind: 'turn_start', seq: ev.seq };
    case 'message_delta': {
      const p = payload as { type?: string; text?: string };
      if (p?.type === 'text' && typeof p.text === 'string') {
        return { kind: 'message', role: 'assistant', text: p.text, seq: ev.seq };
      }
      return null;
    }
    case 'tool_use': {
      const p = payload as { name?: string; input?: unknown; id?: string };
      return { kind: 'tool_use', name: p?.name ?? 'tool', input: p?.input, id: p?.id, seq: ev.seq };
    }
    case 'tool_result': {
      const p = payload as { tool_use_id?: string; content?: unknown };
      return { kind: 'tool_result', toolUseId: p?.tool_use_id, content: p?.content, seq: ev.seq };
    }
    case 'permission_request': {
      const p = payload as { name?: string; input?: unknown; tool_use_id?: string; id?: string };
      return {
        kind: 'permission_request',
        toolUseId: p?.tool_use_id ?? p?.id,
        name: p?.name,
        input: p?.input,
        seq: ev.seq,
      };
    }
    case 'usage':
      return { kind: 'usage', usage: payload, seq: ev.seq };
    case 'turn_end': {
      const p = payload as { is_error?: boolean; total_cost_usd?: number };
      return { kind: 'turn_end', isError: p?.is_error, cost: p?.total_cost_usd, seq: ev.seq };
    }
    case 'error': {
      const p = payload as { error?: string };
      return { kind: 'error', message: p?.error ?? 'error', details: payload, seq: ev.seq };
    }
    case 'system': {
      const p = payload as { type?: string; subtype?: string };
      return { kind: 'system', text: [p?.type, p?.subtype].filter(Boolean).join('/'), seq: ev.seq };
    }
    default:
      return null;
  }
}

/**
 * Normalize a raw frame from a vendor remote-share session (browser-
 * direct mode). Vendor schemas are not yet stable; v1 attempts the
 * Anthropic shape (which is what claude `--output-format stream-json`
 * uses) and falls through with synthetic seq numbers.
 *
 * `nextSeq` is a ref-like getter so the caller can keep monotonic
 * ordering across calls; we don't manage state here.
 */
export function normalizeVendorFrame(raw: unknown, seq: number): UICard | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { type?: string; message?: any; subtype?: string };
  switch (r.type) {
    case 'system':
      return { kind: 'system', text: [r.type, r.subtype].filter(Boolean).join('/'), seq };
    case 'assistant': {
      const content = r.message?.content;
      if (!Array.isArray(content)) return null;
      const text = content.filter((c: any) => c.type === 'text').map((c: any) => c.text).join('');
      if (text) return { kind: 'message', role: 'assistant', text, seq };
      const toolUse = content.find((c: any) => c.type === 'tool_use');
      if (toolUse) return { kind: 'tool_use', name: toolUse.name, input: toolUse.input, id: toolUse.id, seq };
      return null;
    }
    case 'user': {
      const content = r.message?.content;
      if (!Array.isArray(content)) return null;
      const toolResult = content.find((c: any) => c.type === 'tool_result');
      if (toolResult) return { kind: 'tool_result', toolUseId: toolResult.tool_use_id, content: toolResult.content, seq };
      return null;
    }
    case 'result':
      return { kind: 'turn_end', seq };
    default:
      return null;
  }
}

function parseIfString(p: unknown): unknown {
  if (typeof p === 'string') {
    try { return JSON.parse(p); } catch { return p; }
  }
  return p;
}
